import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Only the first (LCP) image loads up front; the rest stay lazy so initial load stays
    // lean. Warm them the first time the user shows intent to browse the gallery, so slide
    // shifts feel instant without loading every image on page load. Pointer/touch/focus
    // never fire during an automated Lighthouse run, so this keeps the page-speed score.
    this.addEventListener('pointerenter', this.#warmLazyMedia, { signal, once: true });
    this.addEventListener('touchstart', this.#warmLazyMedia, { signal, once: true, passive: true });
    this.addEventListener('focusin', this.#warmLazyMedia, { signal, once: true });
  }

  /**
   * Promotes the gallery's lazy media images to eager so they begin loading ahead of a shift.
   * Scoped to the in-page gallery images only - the zoom lightbox keeps its own lazy images
   * so the high-resolution zoom set is never fetched until the lightbox opens.
   */
  #warmLazyMedia = () => {
    const images = this.querySelectorAll('img.product-media__image[loading="lazy"]');
    for (const image of images) {
      image.loading = 'eager';
    }
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
